/**
 * =========================================================
 * Social Media Hub - Premium Footer
 * =========================================================
 *
 * Modern, sleek footer with premium visual design
 * and polished user experience.
 */

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React base styles
import typography from "assets/theme/base/typography";

function Footer({ company, links }) {
  const { href, name } = company;
  const { size } = typography;

  const renderLinks = () =>
    links.map((link, index) => (
      <MDBox
        key={link.name}
        component="li"
        sx={{
          lineHeight: 1,
          "&:not(:last-child)::after": {
            content: '"·"',
            color: "rgba(0, 0, 0, 0.3)",
            mx: 1.5,
          },
        }}
      >
        <Link
          href={link.href}
          target="_blank"
          rel="noreferrer"
          sx={{
            textDecoration: "none",
            transition: "color 0.2s ease",
            "&:hover": {
              color: "#6366f1 !important",
            },
          }}
        >
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{
              fontSize: "0.8rem",
              "&:hover": {
                color: "#6366f1",
              },
            }}
          >
            {link.name}
          </MDTypography>
        </Link>
      </MDBox>
    ));

  return (
    <MDBox
      width="100%"
      display="flex"
      flexDirection={{ xs: "column", lg: "row" }}
      justifyContent="space-between"
      alignItems="center"
      px={1.5}
      py={2}
      sx={{
        borderTop: "1px solid rgba(0, 0, 0, 0.04)",
        mt: "auto",
      }}
    >
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexWrap="wrap"
        color="text"
        fontSize={size.sm}
        px={1.5}
      >
        <MDTypography
          variant="button"
          fontWeight="regular"
          color="text"
          sx={{
            fontSize: "0.8rem",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          © {new Date().getFullYear()}, crafted with
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.1)" },
              },
            }}
          >
            <Icon
              sx={{
                fontSize: "1rem !important",
                color: "#ef4444",
                verticalAlign: "middle",
              }}
            >
              favorite
            </Icon>
          </Box>
          by
          <Link
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
              textDecoration: "none",
              fontWeight: 600,
              color: "inherit",
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#6366f1 !important",
              },
            }}
          >
            {name}
          </Link>
        </MDTypography>
      </MDBox>
      <MDBox
        component="ul"
        sx={({ breakpoints }) => ({
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          listStyle: "none",
          mt: { xs: 1.5, lg: 0 },
          mb: 0,
          p: 0,

          [breakpoints.up("lg")]: {
            mt: 0,
          },
        })}
      >
        {renderLinks()}
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of Footer
Footer.defaultProps = {
  company: { href: "https://thariqhussain.com/", name: "Thariq Hussain" },
  links: [
    { href: "https://thariqhussain.com/", name: "Thariq Hussain" },
    { href: "https://thariqhussain.com/about", name: "About Us" },
    { href: "https://thariqhussain.com/blog", name: "Blog" },
    { href: "https://thariqhussain.com/license", name: "License" },
  ],
};

// Typechecking props for the Footer
Footer.propTypes = {
  company: PropTypes.objectOf(PropTypes.string),
  links: PropTypes.arrayOf(PropTypes.object),
};

export default Footer;
